import React from 'react';
import { X, TrendingUp, TrendingDown, Clock, DollarSign, Target, Calendar, CheckCircle, XCircle, Activity, FileText } from 'lucide-react';
import { Button } from './ui/button';
import CryptoIcon from './CryptoIcon';

const TradeDetailModal = ({ trade, type, onClose }) => {
  if (!trade) return null;

  const renderBinaryTradeDetails = () => (
    <div className="space-y-4">
      {/* COMPREHENSIVE SIMPLE SUMMARY - Top Section */}
      <div className={`rounded-2xl p-6 border-2 ${
        trade.outcome === 'win' 
          ? 'bg-gradient-to-br from-green-900/50 via-emerald-900/40 to-green-900/50 border-green-500/60' 
          : trade.outcome === 'loss'
          ? 'bg-gradient-to-br from-red-900/50 via-rose-900/40 to-red-900/50 border-red-500/60'
          : 'bg-gradient-to-br from-cyan-900/50 via-blue-900/40 to-cyan-900/50 border-cyan-500/60'
      }`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          {trade.outcome === 'win' ? (
            <div className="relative">
              <CheckCircle className="w-20 h-20 text-green-400" />
              <div className="absolute inset-0 bg-green-400/20 blur-xl rounded-full animate-pulse"></div>
            </div>
          ) : trade.outcome === 'loss' ? (
            <div className="relative">
              <XCircle className="w-20 h-20 text-red-400" />
              <div className="absolute inset-0 bg-red-400/20 blur-xl rounded-full animate-pulse"></div>
            </div>
          ) : (
            <div className="relative">
              <Activity className="w-20 h-20 text-cyan-400 animate-pulse" />
              <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-full"></div>
            </div>
          )}
        </div>

        {/* Trade Summary Header */}
        <div className="text-center mb-6">
          <div className="text-3xl font-black text-white mb-2">
            {trade.outcome === 'win' ? '🎉 WINNING TRADE' : trade.outcome === 'loss' ? '💔 LOSING TRADE' : '⏳ ACTIVE TRADE'}
          </div>
          <div className="text-sm text-gray-300">
            {trade.pair} • {trade.direction?.toUpperCase()} • {trade.duration}
          </div>
        </div>

        {/* BOLD WIN ANNOUNCEMENT */}
        {trade.outcome === 'win' && trade.profit > 0 && (
          <div className="bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 rounded-xl p-6 border-2 border-green-400/50 mb-4">
            <div className="text-center">
              <div className="text-sm text-green-300 font-bold mb-2 uppercase tracking-wider">
                🏆 Winning Amount
              </div>
              <div className="text-6xl font-black text-green-400 mb-3 drop-shadow-[0_0_15px_rgba(74,222,128,0.5)]">
                Won + ${trade.profit.toFixed(2)}
              </div>
              <div className="text-xl font-bold text-green-300">
                Total Return: ${(trade.amount + trade.profit).toFixed(2)} USDT
              </div>
            </div>
          </div>
        )}

        {/* Loss Display */}
        {trade.outcome === 'loss' && trade.profit < 0 && (
          <div className="bg-gradient-to-r from-red-500/20 via-rose-500/20 to-red-500/20 rounded-xl p-6 border-2 border-red-400/50 mb-4">
            <div className="text-center">
              <div className="text-sm text-red-300 font-bold mb-2 uppercase tracking-wider">
                Loss Amount
              </div>
              <div className="text-5xl font-black text-red-400 mb-3">
                Lost -${Math.abs(trade.profit).toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Simple Summary Grid */}
        <div className="grid grid-cols-2 gap-3 bg-slate-900/50 rounded-xl p-4 border border-white/10">
          <div className="text-center">
            <div className="text-xs text-gray-400 mb-1">Investment</div>
            <div className="text-lg font-black text-white">${trade.amount?.toFixed(2)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400 mb-1">ROI</div>
            <div className="text-lg font-black text-yellow-400">{trade.roi_percentage}%</div>
          </div>
          <div className="text-center col-span-2">
            <div className="text-xs text-gray-400 mb-1">Status</div>
            <div className={`text-base font-bold ${
              trade.outcome === 'win' ? 'text-green-400' : trade.outcome === 'loss' ? 'text-red-400' : 'text-cyan-400'
            }`}>
              {trade.status === 'active' ? 'Trade in Progress' : `Completed at ${trade.completed_at ? new Date(trade.completed_at).toLocaleString() : ''}`}
            </div>
          </div>
        </div>

        {/* Congratulations/Condolences Message */}
        {trade.outcome === 'win' && (
          <div className="text-center mt-4 text-green-300 font-semibold text-sm">
            🎊 Congratulations! You predicted the market correctly!
          </div>
        )}
        {trade.outcome === 'loss' && (
          <div className="text-center mt-4 text-red-300 font-semibold text-sm">
            💪 Don't give up! Better luck on your next trade!
          </div>
        )}
      </div>

      {/* Detailed Sections Divider */}
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
        <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Detailed Information</span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
      </div>

      {/* Trading Pair */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-gray-400">Trading Pair</span>
          {trade.pair && (
            <div className="flex items-center gap-2">
              <CryptoIcon symbol={trade.pair.split('/')[0]} size={24} />
              <span className="font-bold text-white">{trade.pair}</span>
            </div>
          )}
        </div>
        
        {/* Direction */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Direction</span>
          <div className="flex items-center gap-2">
            {trade.direction === 'up' ? (
              <>
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="font-bold text-green-400">UP</span>
              </>
            ) : (
              <>
                <TrendingDown className="w-5 h-5 text-red-400" />
                <span className="font-bold text-red-400">DOWN</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Investment & Returns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-400">Investment Amount</span>
          </div>
          <div className="text-2xl font-black text-white">${trade.amount?.toFixed(2)}</div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-gray-400">ROI Percentage</span>
          </div>
          <div className="text-2xl font-black text-yellow-400">{trade.roi_percentage}%</div>
        </div>

        <div className={`rounded-xl p-5 border-2 col-span-2 ${
          trade.profit > 0 
            ? 'bg-gradient-to-br from-green-900/40 to-emerald-900/40 border-green-500/60' 
            : trade.profit < 0 
            ? 'bg-gradient-to-br from-red-900/40 to-rose-900/40 border-red-500/60'
            : 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-cyan-500/30'
        }`}>
          <div className="flex items-center gap-2 mb-3">
            {trade.profit > 0 ? (
              <TrendingUp className="w-6 h-6 text-green-400" />
            ) : trade.profit < 0 ? (
              <TrendingDown className="w-6 h-6 text-red-400" />
            ) : (
              <DollarSign className="w-6 h-6 text-gray-400" />
            )}
            <span className="text-sm text-gray-300 font-semibold">🎯 TRADE RESULT</span>
          </div>
          <div className={`text-5xl font-black mb-2 ${
            trade.profit > 0 ? 'text-green-400' : trade.profit < 0 ? 'text-red-400' : 'text-gray-400'
          }`}>
            {trade.profit > 0 ? `Won + $${trade.profit?.toFixed(2)}` : trade.profit < 0 ? `Lost -$${Math.abs(trade.profit)?.toFixed(2)}` : 'Pending'}
          </div>
          {trade.profit > 0 && (
            <div className="text-base text-green-300 font-semibold">
              Total Return: ${(trade.amount + trade.profit).toFixed(2)} USDT
            </div>
          )}
          {trade.profit !== 0 && (
            <div className="text-sm text-gray-400 mt-2 flex items-center gap-2">
              {trade.profit > 0 ? '🎉 Congratulations on your winning trade!' : '😔 Better luck next time!'}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-400">Duration</span>
          </div>
          <div className="text-2xl font-black text-white">{trade.duration}</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
        <div className="text-sm font-bold text-cyan-400 mb-3">Timeline</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Start Time</span>
            <span className="text-sm text-white">{trade.start_time ? new Date(trade.start_time).toLocaleString() : 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Expiry Time</span>
            <span className="text-sm text-white">{trade.expiry_time ? new Date(trade.expiry_time).toLocaleString() : 'N/A'}</span>
          </div>
          {trade.completed_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Completed At</span>
              <span className="text-sm text-white">{new Date(trade.completed_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Trade ID */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-3 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Trade ID</span>
          <span className="text-xs font-mono text-cyan-400">{trade.trade_id || trade._id || 'N/A'}</span>
        </div>
      </div>

      {/* Full Trade Report - Simple Text Format */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border-2 border-cyan-500/40">
        <div className="text-sm font-bold text-cyan-400 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Complete Trade Report
        </div>
        <div className="space-y-1 text-sm font-mono text-gray-300 bg-slate-900/50 p-4 rounded-lg">
          <div>═══════════════════════════════════════</div>
          <div className="text-cyan-400 font-bold text-center">BINARY TRADE REPORT</div>
          <div>═══════════════════════════════════════</div>
          <div className="my-2"></div>
          <div>Trade ID: {trade.trade_id || 'N/A'}</div>
          <div>Status: {trade.status?.toUpperCase() || 'N/A'}</div>
          <div>Outcome: {trade.outcome ? trade.outcome.toUpperCase() : 'PENDING'}</div>
          <div className="my-2"></div>
          <div>Trading Pair: {trade.pair || 'N/A'}</div>
          <div>Direction: {trade.direction?.toUpperCase() || 'N/A'}</div>
          <div>Duration: {trade.duration || 'N/A'}</div>
          <div className="my-2"></div>
          <div>Investment Amount: ${trade.amount?.toFixed(2) || '0.00'} USDT</div>
          <div>ROI Percentage: {trade.roi_percentage || 0}%</div>
          {trade.profit !== undefined && (
            <>
              <div className={trade.profit > 0 ? 'text-green-400 font-bold' : trade.profit < 0 ? 'text-red-400 font-bold' : ''}>
                Result: {trade.profit > 0 ? `Won + $${trade.profit.toFixed(2)} USDT` : trade.profit < 0 ? `Lost -$${Math.abs(trade.profit).toFixed(2)} USDT` : 'Pending'}
              </div>
              {trade.profit > 0 && (
                <div className="text-green-400 font-bold">Total Return: ${(trade.amount + trade.profit).toFixed(2)} USDT</div>
              )}
            </>
          )}
          <div className="my-2"></div>
          <div>Start Time: {trade.start_time ? new Date(trade.start_time).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }) : 'N/A'}</div>
          <div>Expiry Time: {trade.expiry_time ? new Date(trade.expiry_time).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }) : 'N/A'}</div>
          {trade.completed_at && (
            <div>Completed At: {new Date(trade.completed_at).toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}</div>
          )}
          <div className="my-2"></div>
          <div>═══════════════════════════════════════</div>
          <div className="text-center text-xs text-gray-500 mt-2">
            Report Generated: {new Date().toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );

  const renderArbitrageDetails = () => (
    <div className="space-y-4">
      {/* Investment Status Banner */}
      <div className={`rounded-xl p-4 text-center ${
        trade.status === 'completed'
          ? 'bg-gradient-to-r from-green-900/40 to-emerald-900/40 border-2 border-green-500/50'
          : 'bg-gradient-to-r from-purple-900/40 to-blue-900/40 border-2 border-purple-500/50'
      }`}>
        <div className="text-2xl font-black text-white mb-1">
          {trade.package_name} Package
        </div>
        <div className="text-sm text-gray-300">
          {trade.status === 'active' ? 'Investment Active' : 'Investment Completed'}
        </div>
      </div>

      {/* Investment Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-400">Initial Investment</span>
          </div>
          <div className="text-2xl font-black text-white">${trade.amount?.toLocaleString()}</div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-xs text-gray-400">Current Returns</span>
          </div>
          <div className="text-2xl font-black text-green-400">+${trade.total_returns?.toLocaleString() || '0'}</div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-gray-400">Profit Percentage</span>
          </div>
          <div className="text-2xl font-black text-yellow-400">+{trade.profit_percentage?.toFixed(2) || '0'}%</div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-400">Total Value</span>
          </div>
          <div className="text-2xl font-black text-cyan-400">${((trade.amount || 0) + (trade.total_returns || 0)).toLocaleString()}</div>
        </div>
      </div>

      {/* Period */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
        <div className="text-sm font-bold text-cyan-400 mb-3">Investment Period</div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Start Date</span>
            <span className="text-sm text-white">{trade.start_date ? new Date(trade.start_date).toLocaleDateString() : 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">End Date</span>
            <span className="text-sm text-white">{trade.end_date ? new Date(trade.end_date).toLocaleDateString() : 'N/A'}</span>
          </div>
          {trade.completed_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Completed At</span>
              <span className="text-sm text-white">{new Date(trade.completed_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Investment ID */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-3 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Investment ID</span>
          <span className="text-xs font-mono text-cyan-400">{trade.investment_id || trade._id || 'N/A'}</span>
        </div>
      </div>
    </div>
  );

  const renderSpotTradeDetails = () => (
    <div className="space-y-4">
      {/* Trade Type Banner */}
      <div className="rounded-xl p-4 text-center bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border-2 border-cyan-500/50">
        <div className="text-2xl font-black text-white mb-1">
          Spot {trade.type === 'buy' ? 'Buy' : 'Sell'} Order
        </div>
        <div className="text-sm text-gray-300">
          {trade.status || 'Completed'}
        </div>
      </div>

      {/* Order Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-gray-400">Amount</span>
          </div>
          <div className="text-2xl font-black text-white">{trade.amount || 'N/A'}</div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-yellow-400" />
            <span className="text-xs text-gray-400">Price</span>
          </div>
          <div className="text-2xl font-black text-yellow-400">${trade.price || 'N/A'}</div>
        </div>
      </div>

      {/* Timestamp */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-4 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Timestamp</span>
          <span className="text-sm text-white">{trade.timestamp ? new Date(trade.timestamp).toLocaleString() : 'N/A'}</span>
        </div>
      </div>

      {/* Transaction ID */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-3 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">Transaction ID</span>
          <span className="text-xs font-mono text-cyan-400">{trade.transaction_id || trade._id || 'N/A'}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500/50 rounded-2xl p-4 sm:p-6 max-w-2xl w-full my-8 shadow-2xl shadow-cyan-500/20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-black text-cyan-400">Trade Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-cyan-500/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-cyan-400" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
          {type === 'binary' && renderBinaryTradeDetails()}
          {type === 'arbitrage' && renderArbitrageDetails()}
          {type === 'spot' && renderSpotTradeDetails()}
        </div>

        {/* Close Button */}
        <div className="mt-6">
          <Button
            onClick={onClose}
            className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold"
          >
            Close
          </Button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(6, 182, 212, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(6, 182, 212, 0.7);
        }
      `}</style>
    </div>
  );
};

export default TradeDetailModal;
